import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CompatibilityBadge, LicenseSummary, PriceAndRights, Button } from '@3dspinit/ui';
import { api, formatPrice, runtimeUrlFor, type FeedItem, ApiError } from '../lib/api';
import { ViewerCanvas } from '../components/ViewerCanvas';
import { GuestGate } from '../components/GuestGate';
import { PreviewCounter } from '../components/PreviewCounter';
import { useSession } from '../context/SessionContext';
import { setPendingAction } from '../lib/pendingActions';

type Access = {
  canViewRuntime?: boolean;
  isOwner?: boolean;
  hasEntitlement?: boolean;
};

async function fetchRuntimeBlob(assetId: string): Promise<string> {
  const res = await fetch(runtimeUrlFor(assetId), {
    credentials: 'include',
    headers: { Accept: 'model/gltf-binary,*/*' },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    let message = `Runtime unavailable (${res.status})`;
    try {
      const body = JSON.parse(text) as { message?: string; error?: string };
      message = body.message ?? body.error ?? message;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, message, 'RUNTIME_FORBIDDEN');
  }
  const buf = await res.arrayBuffer();
  const blobUrl = URL.createObjectURL(new Blob([buf], { type: 'model/gltf-binary' }));
  return `${blobUrl}#runtime.glb`;
}

function primaryAction(input: {
  sessionState: string;
  authenticated: boolean;
  hasEntitlement: boolean;
  isOwner: boolean;
  priceCents?: number | null;
  canViewRuntime: boolean;
}) {
  if (!input.authenticated) {
    return { label: 'Sign in to acquire', hint: 'Browse remains open; review rights and cost first.' };
  }
  if (input.sessionState === 'SIGNED_IN_UNVERIFIED') {
    return { label: 'Verify with Keyra', hint: 'Complete identity verification to acquire or upload.' };
  }
  if (input.isOwner) {
    return { label: 'Open workspace', hint: 'Inspect, adjust presentation, then save to Library.', path: `/workspace` };
  }
  if (input.hasEntitlement) {
    return { label: 'Use in compatible world', hint: 'Select authorized version and destination (R2).' };
  }
  if (input.priceCents && input.priceCents > 0) {
    return { label: 'Review purchase', hint: 'Confirm total, version, delivery mode and terms.' };
  }
  return { label: 'Add to Library', hint: 'Controlled use is not source export.' };
}

export function SpinPage() {
  const { assetId = '' } = useParams();
  const navigate = useNavigate();
  const { sessionState, authenticated, loading: sessionLoading, startKeyra } = useSession();
  const [asset, setAsset] = useState<
    (FeedItem & {
      access?: Access;
      listing?: { id: string; priceCents: number; status: string } | null;
      compatibility?: Array<{ destinationId: string; status: string }>;
    }) | null
  >(null);
  const [grantCount, setGrantCount] = useState(0);
  const [runtimeUrl, setRuntimeUrl] = useState<string | null>(null);
  const [gate, setGate] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoRotate, setAutoRotate] = useState(false);
  const [viewerStatus, setViewerStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [favorited, setFavorited] = useState(false);

  useEffect(() => {
    if (sessionLoading || !assetId) return;

    let cancelled = false;
    let objectUrl: string | null = null;

    async function load() {
      try {
        setError(null);
        setGate(false);
        setViewerStatus('loading');
        setRuntimeUrl(null);

        const { asset: detail } = await api.getAsset(assetId);
        if (cancelled) return;
        setAsset(detail);

        if (!detail.access?.canViewRuntime) {
          if (authenticated) {
            setViewerStatus('error');
            setError('You do not have access to spin this object yet.');
            return;
          }
          const grant = await api.requestPreviewGrant(assetId);
          if (cancelled) return;
          setGrantCount(grant.grantCount ?? 0);
          if (!grant.granted) {
            setViewerStatus('error');
            setError('Preview was not granted');
            return;
          }
        }

        objectUrl = await fetchRuntimeBlob(assetId);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl.split('#')[0]!);
          return;
        }
        setRuntimeUrl(objectUrl);
        setViewerStatus('ready');
        if (authenticated) {
          void api.getFavoriteStatus(assetId).then((r) => setFavorited(r.favorited)).catch(() => undefined);
        }
      } catch (e) {
        if (cancelled) return;
        if (e instanceof ApiError && e.code === 'ACCOUNT_REQUIRED') {
          setPendingAction({ type: 'view_asset', assetId });
          setGate(true);
          setViewerStatus('idle');
          return;
        }
        setViewerStatus('error');
        setError(e instanceof Error ? e.message : 'Failed to open object');
      }
    }

    void load();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl.split('#')[0]!);
    };
  }, [assetId, authenticated, sessionLoading]);

  const price = formatPrice(asset?.priceCents);
  const action = useMemo(
    () =>
      primaryAction({
        sessionState,
        authenticated,
        hasEntitlement: Boolean(asset?.access?.hasEntitlement),
        isOwner: Boolean(asset?.access?.isOwner),
        priceCents: asset?.priceCents,
        canViewRuntime: Boolean(asset?.access?.canViewRuntime),
      }),
    [sessionState, authenticated, asset],
  );

  function onPrimaryAction() {
    if (!authenticated) {
      setPendingAction({ type: 'view_asset', assetId });
      void startKeyra(`/spin/${assetId}`);
      return;
    }
    if (sessionState === 'SIGNED_IN_UNVERIFIED') {
      void startKeyra(`/spin/${assetId}`);
      return;
    }
    if (asset?.access?.isOwner) {
      navigate(`/workspace?asset=${assetId}`);
      return;
    }
    if (asset?.access?.hasEntitlement) {
      navigate('/connect');
      return;
    }
    if (asset?.listing?.id && (asset.priceCents ?? 0) > 0) {
      navigate(`/checkout/${asset.listing.id}`);
      return;
    }
    void startKeyra(`/spin/${assetId}`);
  }

  return (
    <div className="viewer-shell">
      <div className="page">
        <div className="detail-layout">
          <div>
            <ViewerCanvas
              runtimeUrl={runtimeUrl}
              posterUrl={asset?.posterUrl}
              title={asset?.title}
              autoRotate={autoRotate}
              status={viewerStatus}
              error={error}
            />
            <div className="row" style={{ marginTop: '0.75rem' }}>
              {!authenticated && <PreviewCounter used={grantCount} />}
              <button type="button" className="btn" onClick={() => setAutoRotate((v) => !v)}>
                {autoRotate ? 'Pause rotation' : 'Auto-rotate'}
              </button>
              {authenticated ? (
                <button
                  type="button"
                  className="btn"
                  aria-pressed={favorited}
                  onClick={() => {
                    void (favorited ? api.removeFavorite(assetId) : api.addFavorite(assetId))
                      .then((r) => setFavorited(r.favorited))
                      .catch(() => undefined);
                  }}
                >
                  {favorited ? 'Saved ★' : 'Save ★'}
                </button>
              ) : null}
              {asset?.access?.isOwner ? (
                <Link className="btn" to={`/workspace?asset=${assetId}`}>
                  Workspace
                </Link>
              ) : null}
              <Link className="btn ghost" to="/">
                Back to Explore
              </Link>
            </div>
          </div>

          <aside className="detail-panel" aria-labelledby="asset-title">
            <div>
              <h1 id="asset-title" style={{ margin: 0, fontFamily: 'var(--font-display)', letterSpacing: '-0.03em', fontSize: '1.5rem' }}>
                {asset?.title ?? 'Object'}
              </h1>
              {asset?.creator?.username && (
                <p className="muted" style={{ margin: '0.35rem 0 0' }}>
                  <Link to={`/channel/${asset.creator.username}`}>@{asset.creator.username}</Link>
                </p>
              )}
            </div>

            <PriceAndRights
              priceLabel={price}
              licenseLabel={price ? 'Controlled use license' : 'Free controlled use'}
            />
            <CompatibilityBadge
              status={(asset?.compatibility?.[0]?.status as 'untested' | 'passed' | 'failed' | 'stale') ?? 'untested'}
              destination={asset?.compatibility?.[0]?.destinationId ?? null}
            />

            <LicenseSummary
              actions={['Inspect preview', price ? 'Acquire license' : 'Add to library']}
              deliveryMode="Controlled use (default)"
              limitations={['Source export not included unless separately offered']}
            />

            <p className="muted" style={{ fontSize: '0.875rem', margin: 0 }}>{action.hint}</p>

            <div style={{ width: '100%' }}>
              <Button variant="primary" onClick={onPrimaryAction}>
                {action.label}
              </Button>
            </div>

            <details>
              <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Technical details</summary>
              <dl className="muted" style={{ fontSize: '0.875rem', margin: '0.5rem 0 0' }}>
                <dt>Version</dt>
                <dd>Current published preview</dd>
                <dt>Compatibility</dt>
                <dd>Unknown — no destination test on file</dd>
                <dt>Dimensions</dt>
                <dd>Unknown</dd>
              </dl>
            </details>
          </aside>
        </div>
        {error && viewerStatus === 'error' && !asset?.posterUrl ? <p className="err">{error}</p> : null}
      </div>
      <GuestGate
        open={gate}
        onDismiss={() => {
          setGate(false);
          navigate('/');
        }}
      />
    </div>
  );
}
